import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService } from '@cosmos/angular/utils';
import { InboundEmailAddresses, Project, ReportConfig, SelfServeProjectResponse } from '@cosmos/shared';
import { catchError, map, Observable } from 'rxjs';
import { environment } from '../../../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProjectsService {

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
  ) { }

    find(query = ''): Observable<HttpResponse<Project[]>> {
      return this.http
        .get<Project[]>(`${environment.API_URL}/projects${query}`, {observe: 'response'})
          .pipe(
            map(response => {return response;}),
            catchError( (error) =>{this.errorService.handleError(error); throw error;})
          );
    }

    findById(id: string, query = ''): Observable<HttpResponse<Project>> {
      return this.http
        .get<Project>(`${environment.API_URL}/projects/${id}${query}`, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
    }

    create(body: Project) {
      return this.http
        .post(`${environment.API_URL}/projects`, body, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
    }

    createSelfServe(name: string): Observable<HttpResponse<SelfServeProjectResponse>> {
      return this.http
        .post<SelfServeProjectResponse>(`${environment.API_URL}/projects/self-serve`, { name }, { observe: 'response' })
        .pipe(
          map(response => response),
          catchError((error) => { this.errorService.handleError(error); throw error; })
        );
    }

    update(body: any) {
      return this.http
        .patch(`${environment.API_URL}/projects/${body[0]}`, body[1], {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
    }

    delete(id: string) {
      return this.http
        .delete(`${environment.API_URL}/projects/${id}`, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
    }

    getReportConfig(projectId: string): Observable<HttpResponse<ReportConfig>> {
      return this.http
        .get<ReportConfig>(`${environment.API_URL}/projects/${projectId}/report-config`, {observe: 'response'})
        .pipe(
          map(response => response),
          catchError((error) => { this.errorService.handleError(error); throw error; })
        );
    }

    updateReportConfig(projectId: string, config: Partial<ReportConfig>): Observable<HttpResponse<Project>> {
      return this.http
        .patch<Project>(`${environment.API_URL}/projects/${projectId}/report-config`, config, {observe: 'response'})
        .pipe(
          map(response => response),
          catchError((error) => { this.errorService.handleError(error); throw error; })
        );
    }

    getMonitoringConfig(projectId: string): Observable<HttpResponse<{ defaultMeasurementStarted?: boolean }>> {
      return this.http
        .get<{ defaultMeasurementStarted?: boolean }>(`${environment.API_URL}/projects/${projectId}/monitoring-config`, {observe: 'response'})
        .pipe(
          map(response => response),
          catchError((error) => { this.errorService.handleError(error); throw error; })
        );
    }

    updateMonitoringConfig(projectId: string, config: { defaultMeasurementStarted?: boolean }): Observable<HttpResponse<Project>> {
      return this.http
        .patch<Project>(`${environment.API_URL}/projects/${projectId}/monitoring-config`, config, {observe: 'response'})
        .pipe(
          map(response => response),
          catchError((error) => { this.errorService.handleError(error); throw error; })
        );
    }

    activateEmailInbound(projectId: string): Observable<HttpResponse<{ token: string; addresses: InboundEmailAddresses }>> {
      return this.http
        .post<{ token: string; addresses: InboundEmailAddresses }>(
          `${environment.API_URL}/projects/${projectId}/analytics-config/activate-email-inbound`,
          {},
          { observe: 'response' },
        )
        .pipe(
          map(response => response),
          catchError((error) => { this.errorService.handleError(error); throw error; })
        );
    }

    regenerateEmailInboundToken(projectId: string): Observable<HttpResponse<{ token: string; addresses: InboundEmailAddresses }>> {
      return this.http
        .post<{ token: string; addresses: InboundEmailAddresses }>(
          `${environment.API_URL}/projects/${projectId}/analytics-config/regenerate-email-inbound-token`,
          {},
          { observe: 'response' },
        )
        .pipe(
          map(response => response),
          catchError((error) => { this.errorService.handleError(error); throw error; })
        );
    }

    updateInboundEmailAllowList(
      projectId: string,
      allowListEnabled: boolean,
      allowList: string[],
    ): Observable<HttpResponse<{ ok: boolean }>> {
      return this.http
        .patch<{ ok: boolean }>(
          `${environment.API_URL}/projects/${projectId}/analytics-config/email-inbound/allow-list`,
          { allowListEnabled, allowList },
          { observe: 'response' },
        )
        .pipe(
          map(response => response),
          catchError((error) => { this.errorService.handleError(error); throw error; })
        );
    }

}
